import { isDeviceConnected } from '../device-status'
import type { StateSummary } from '@/types/devices'

export interface DeviceStateResult {
  deviceId: string
}

export type RSSIResult = DeviceStateResult & { rssi: number | null }
export type ConnectionTypeResult = DeviceStateResult & { connectionType: string | null }
export type EoLResult = DeviceStateResult & { spsEoL: boolean | null }
export type FirmwareResult = DeviceStateResult & { firmware: string | null }
export type MqttHostResult = DeviceStateResult & { mqttHost: string | null }
export type SmartBridgeResult = DeviceStateResult & { connectedSmartBridgeSerial: string | null }
export type LastStateResult = DeviceStateResult & { timestamp: string | null }
export type TelemTxTypeResult = DeviceStateResult & { telemTxType: number | null }
export type IsConnectedResult = DeviceStateResult & { isConnected: boolean }
export type BatteryChargingResult = DeviceStateResult & { batteryCharging: boolean | null }
export type BatteryInstalledResult = DeviceStateResult & { batteryInstalled: boolean | null }
export type BatteryLowResult = DeviceStateResult & { batteryLow: boolean | null }
export type BatteryRemainingResult = DeviceStateResult & { batteryRemaining: number | null }
export type ErrorStateResult = DeviceStateResult & { errorState: string[] | null }
export type StudyIdResult = DeviceStateResult & { lastTelemetryStudyId: string | null }

export function getRSSI(deviceId: string, state: StateSummary): RSSIResult {
  if (state?.data?.cloud) {
    return {
      deviceId: deviceId,
      rssi: state.data.cloud?.ap_rssi || null
    }
  }

  return {
    deviceId: deviceId,
    rssi: null
  }
}

/**
 * connection_type is NOT present in the GET /summary/devices response (StateSummary / SummaryStateData).
 * It exists only in the GET /devices/:id response (devices-api/internal/device/device.go StateData),
 * which is handled separately by getDeviceDetailsFromState in src/helpers/devices/stateHelper.js.
 * This function always returns null for the summary flow.
 * TODO: Validate whether this should be removed
 */
export function getConnectionType(deviceId: string, _state: StateSummary): ConnectionTypeResult {
  return { deviceId, connectionType: null }
}

export function getEoL(deviceId: string, state: StateSummary): EoLResult {
  const eol = state?.data?.['bs-8143']?.spsEoL
  if (eol !== undefined) {
    return {
      deviceId: deviceId,
      spsEoL: eol
    }
  }

  return {
    deviceId: deviceId,
    spsEoL: null
  }
}

export function getFirmwareVersion(deviceId: string, state: StateSummary): FirmwareResult {
  if (state?.data) {
    return {
      deviceId,
      firmware: state.data.firmware_version ?? null
    }
  }

  return { deviceId, firmware: null }
}

export function getMqttHost(deviceId: string, state: StateSummary): MqttHostResult {
  if (state?.data) {
    return {
      deviceId,
      mqttHost: state.data.mqtt_host ?? null
    }
  }

  return { deviceId, mqttHost: null }
}

export function getConnectedSmartBridgeSerial(
  deviceId: string,
  state: StateSummary
): SmartBridgeResult {
  return {
    deviceId: deviceId,
    connectedSmartBridgeSerial: state?.data?.hasOwnProperty('gateway_serial')
      ? state.data.gateway_serial
      : null
  }
}

export function getLastState(deviceId: string, state: StateSummary): LastStateResult {
  if (state) {
    return {
      deviceId,
      timestamp: state.timestamp ?? null
    }
  }

  return { deviceId, timestamp: null }
}

export function getTelemTxType(deviceId: string, state: StateSummary): TelemTxTypeResult {
  if (state?.data) {
    return {
      deviceId,
      telemTxType: state.data.telem_tx_type ?? null
    }
  }

  return { deviceId, telemTxType: null }
}

export function getIsConnected(
  deviceId: string,
  state: StateSummary,
  lastCommunicationTime: string | null
): IsConnectedResult {
  return {
    deviceId: deviceId,
    isConnected:
      state?.data?.hasOwnProperty('gateway_serial') && state?.data?.hasOwnProperty('connected')
        ? state.data.connected
        : isDeviceConnected(lastCommunicationTime)
  }
}

export function getBatteryCharging(deviceId: string, state: StateSummary): BatteryChargingResult {
  return {
    deviceId,
    batteryCharging: state?.data?.hasOwnProperty('battery_charging')
      ? (state.data.battery_charging ?? null)
      : null
  }
}

export function getBatteryInstalled(deviceId: string, state: StateSummary): BatteryInstalledResult {
  return {
    deviceId: deviceId,
    batteryInstalled: state?.data?.hasOwnProperty('battery_installed')
      ? state.data.battery_installed
      : null
  }
}

export function getBatteryLow(deviceId: string, state: StateSummary): BatteryLowResult {
  return {
    deviceId: deviceId,
    batteryLow: state?.data?.hasOwnProperty('battery_low') ? state.data.battery_low : null
  }
}

export function getBatteryRemaining(deviceId: string, state: StateSummary): BatteryRemainingResult {
  return {
    deviceId,
    batteryRemaining: state?.data?.hasOwnProperty('battery_remaining')
      ? (state.data.battery_remaining ?? null)
      : null
  }
}

export function getErrorState(deviceId: string, state: StateSummary): ErrorStateResult {
  return {
    deviceId,
    errorState: state?.data?.hasOwnProperty('error_state') ? (state.data.error_state ?? null) : null
  }
}

export function getStudyId(deviceId: string, state: StateSummary): StudyIdResult {
  return {
    deviceId,
    lastTelemetryStudyId: state?.data?.hasOwnProperty('study_id')
      ? (state.data.study_id ?? null)
      : null
  }
}
